import { z } from 'zod';

/**
 * Upload links (PLATFORM_EXPANSION.md §2.5, X5.2). A `/u/<token>` page lets
 * family or staff upload photos to a photo-frame screen. Uploads are personal
 * data: consent text, retention, EXIF/GPS stripping, size/type limits, a
 * moderation queue and per-link revoke are all part of the contract, not
 * optional extras. The token is 128-bit random like a screen `public_id`.
 *
 * Server-side obligations (never client-side): EXIF/GPS stripping, resize,
 * type/size enforcement, retention purge and moderation gating before anything
 * reaches a display.
 */

export const UPLOAD_LINK_STATUSES = ['active', 'revoked', 'expired'] as const;
export const UploadLinkStatusSchema = z.enum(UPLOAD_LINK_STATUSES);
export type UploadLinkStatus = z.infer<typeof UploadLinkStatusSchema>;

export const UploadLinkSchema = z.object({
  tenant: z.string().min(1),
  /** The screen (photo frame) uploads land on. */
  screen: z.string().min(1),
  /** 128-bit random, ≥22 base64url chars; rotated and revocable. */
  token: z.string().min(22).max(128).regex(/^[A-Za-z0-9_-]+$/),
  status: UploadLinkStatusSchema.default('active'),
  expires_at: z.string().datetime().optional(),
  /** Consent copy shown before the upload control; required. */
  consent_text: z.string().min(1),
  /** Server-enforced limits. */
  max_bytes: z.number().int().min(1).default(20 * 1024 * 1024),
  allowed_mime: z.array(z.string().min(1)).min(1).default(['image/jpeg', 'image/png', 'image/webp']),
  /** Personal-data retention; the purge job deletes older uploads. */
  retention_days: z.number().int().min(1).max(3650).default(90),
  /** Moderation required before an upload can be displayed. */
  moderation_required: z.boolean().default(true)
});
export type UploadLink = z.infer<typeof UploadLinkSchema>;

/** One uploaded asset, tracked through moderation. */
export const UploadItemSchema = z.object({
  link: z.string().min(1),
  media: z.string().min(1),
  /** EXIF/GPS stripped server-side; recorded so it is auditable. */
  exif_stripped: z.boolean(),
  state: z.enum(['pending', 'approved', 'rejected']).default('pending'),
  uploaded_at: z.string().datetime(),
  /** Purge-after instant derived from the link's retention_days. */
  purge_after: z.string().datetime()
});
export type UploadItem = z.infer<typeof UploadItemSchema>;
